import { useCallback, useEffect, useRef, useState } from "react";
import { BsFilePerson, BsList } from "react-icons/bs";
import { GrClose } from "react-icons/gr";
import { HiShoppingCart } from "react-icons/hi";
import logo from "../../assets/logo.png";
import { useCart } from "../../hooks/useCart";
import CartDrawer from "./cart-drawer";
import DeleteAccountDialog from "./delete-account-dialog";
import LoginSignupDialog from "./login-signup-dialog";

import Menu from "@mui/material/Menu";

import { MdAccountCircle, MdLocationPin } from "react-icons/md";
import { TbChefHat } from "react-icons/tb";

import { GiBackwardTime } from "react-icons/gi";

import { FiSearch } from "react-icons/fi";

import { MdKeyboardBackspace } from "react-icons/md";

import Drawer from "@mui/material/Drawer";

import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useLanguage } from "../../hooks/useLanguage";
import {
  POST_LOGOUT,
  POST_TO_CART,
  POST_USER_DATA,
  TOGGLE_DIALOG
} from "../../store/action";
import "../styles/navbar.css";
import LanguageToggleButton from "./language-toggle-button";
import { openNotificationWithIcon } from "./notification-bar";

import { t } from "../../translations";
import { DELETE, GET, PUT } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";

import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { clearAuthStorage } from "../../utils/authStorageUtils";
import { createLogger } from "../../utils/logger";
import { Aa } from "../functions/Aa";

const logger = createLogger("Navbar");

const CAPACITOR_SERVER_URL = import.meta.env.VITE_CAPACITOR_SERVER_URL ?? "https://panichefs.com";

const openPartnerPage = async () => {
  const url = `${CAPACITOR_SERVER_URL}/become-a-chef`;
  try {
    await Browser.open({ url });
  } catch {
    window.open(url, "_blank");
  }
};

const Navbar = (props) => {
  const [open, setOpen] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  const [, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);

  const openDropdown = Boolean(anchorEl2);
  // const [openDropdown, _] = useState(false);

  const dispatch = useDispatch();
  const zip = useSelector((state) => state.selectedZip);

  const selectedWhen = useSelector((state) => state.selectedWhen);
  const openDialog = useSelector((state) => state.openDialog);

  // Use cart service for cart statistics
  const { getStats } = useCart();
  const cartStats = getStats();

  const setOpenDialog = (data) => {
    dispatch(toggle_dialog(data));
  };

  const userData = useSelector((state) => state.user);

  // Phase 1: Initialize language (common for both logged-in and non-logged-in users)
  // This ensures language is loaded from localStorage/browser before rendering
  const { selectedLang } = useLanguage();

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (open) {
      document.body.classList.add("menu-is-open");
    } else {
      document.body.classList.remove("menu-is-open");
    }
  }, [open]);

  const LogOutUser = useCallback(async () => {
    if (userData?._id && userData?.refresh_token) {
      try {
        await MakeRequestAsync({
          type: PUT,
          url: service_api,
          route: `user/update-user/${userData._id}`,
          data: { pushToken: null },
          token: userData.refresh_token
        });
      } catch (_) {}
    }
    clearAuthStorage();
    logger.log("🔓 User logged out - cleared auth storage");
    dispatch(post_logout());
  }, [dispatch, userData]);

  const hasInitialized = useRef(false);

  const getLatestUserData = useCallback(async () => {
    if (userData && userData?.refresh_token?.length !== 0) {
      try {
        const response = await MakeRequestAsync({
          type: GET,
          url: service_api,
          route: `user/get-one/${userData?._id}`,
          data: null,
          token: userData?.refresh_token
        });
        return dispatch(post_user(decryptSingleData(response.data.user)));
      } catch (err) {
        logger.log(err);
        LogOutUser();
        history.push("/");
      }
    }
  }, [userData, dispatch, LogOutUser, history]);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    getLatestUserData();
    if ((!props?.landing && !zip) || (!props?.landing && zip.length === 0)) {
      setAnchorEl(true);
    }
  }, [getLatestUserData, props?.landing, zip]);

  const isNative = Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android";
  const showBackButton = isNative && location.pathname !== "/";

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  const handleDeleteAccount = useCallback(async () => {
    if (!userData?._id || !userData?.refresh_token) return;
    setDeleteAccountLoading(true);
    try {
      await MakeRequestAsync({
        type: DELETE,
        url: service_api,
        route: `user/delete-account/${userData._id}`,
        data: null,
        token: userData.refresh_token
      });
      clearAuthStorage();
      dispatch(post_logout());
      openNotificationWithIcon(
        "success",
        t("success.account_deleted", selectedLang) || "Your account has been deleted."
      );
      setDeleteAccountDialogOpen(false);
      setOpen(false);
      handleClose2();
      history.push("/");
    } catch (err) {
      logger.error("Delete account failed:", err);
      openNotificationWithIcon(
        "error",
        err?.response?.data?.message ||
          t("errors.delete_account_failed", selectedLang) ||
          "Failed to delete account. Please try again."
      );
    } finally {
      setDeleteAccountLoading(false);
    }
  }, [userData, dispatch, history, selectedLang]);

  const handleClick2 = (event) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleClose2 = () => {
    setAnchorEl2(null);
  };

  const post_to_cart = (data) => {
    dispatch(post_cart(data));
  };

  return (
    <div className="navbar-block">
      <div className="navbar">
        {/* On iOS/Android: show back button when not on home; on web: show logo */}
        {Capacitor.getPlatform() === "web" && (
          <div className="navbar-logo">
            <img
              src={logo}
              alt="logo"
              onClick={() => {
                return history.push("/");
              }}
            />
            <span className="site-name">Pani</span>
          </div>
        )}
        {showBackButton && (
          <button
            type="button"
            className="navbar-back-btn"
            onClick={handleBack}
            aria-label={t("navbar.back", selectedLang)}
          >
            <MdKeyboardBackspace size={28} />
          </button>
        )}

        <div className="navbar-links">
          {!props?.landing && (
            <div className="nav-link-input">
              <FiSearch
                size={30}
                color={"gray"}
                style={{
                  marginLeft: 5
                }}
              />
              <input
                placeholder={props?.placeholder}
                value={props?.textValue}
                onChange={(e) => {
                  props?.onSearch(e.target.value);
                }}
              />
            </div>
          )}
          {/* <Link to="/our-story" className="nav-link">
            {t("navbar.history", selectedLang)}
          </Link> */}
          {userData?.chef === false && userData?.restaurant === false && (
            <Link to="/become-a-chef" className="nav-link">
              {t("navbar.chef", selectedLang)}
            </Link>
          )}
          {(userData?.chef === true || userData?.restaurant === true) && (
            <Link to="/chef/iam-a-chef/home" className="nav-link">
              {t("navbar.dashboard_chef", selectedLang)}
            </Link>
          )}
          {!userData ? (
            <>
              {/* Phase 2: Non-logged-in user specific UI */}
              <button
                className="signin"
                onClick={() => {
                  setOpenDialog(true);
                }}
              >
                {t("navbar.login", selectedLang)}
              </button>
              <button
                className="signup"
                onClick={() => {
                  setOpenDialog(true);
                }}
              >
                {t("navbar.register", selectedLang)}
              </button>
              {/* Language toggle for non-logged-in users */}
              <LanguageToggleButton
                style={{ marginLeft: "10px", fontSize: "12px" }}
              />
            </>
          ) : (
            <>
              <button
                className="cart"
                onClick={() => {
                  setOpenCart(true);
                }}
              >
                <HiShoppingCart size={20} />
                <div className="number-order">
                  <p>{cartStats.itemCount}</p>
                </div>
              </button>
              <button className="cart cart-profile" onClick={handleClick2}>
                <BsFilePerson size={20} />
                <p> {t("navbar.profile", selectedLang)}</p>
              </button>
              {/* Language toggle for logged-in users */}
              <LanguageToggleButton
                style={{ marginLeft: "10px", fontSize: "12px" }}
              />
              {openDropdown && <div className="dropdown-menu"></div>}
            </>
          )}
        </div>
        <div className="drawer-mobile-icon">
          {userData && Capacitor.isNativePlatform() && (
            <button
              className="mobile-cart-btn"
              onClick={() => setOpenCart(true)}
              aria-label="Cart"
            >
              <HiShoppingCart size={18} />
              <span className="mobile-cart-count">
                <span className="mobile-cart-circle">{cartStats.itemCount}</span>
              </span>
            </button>
          )}
          <button
            className="mobile-hamburger-btn"
            onClick={() => setOpen(true)}
            aria-label="Menu"
          >
            <BsList size={30} />
          </button>
        </div>
      </div>
      <Drawer
        anchor={"right"}
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: "70%",
            maxWidth: "280px",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
            boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
            background: "#000",
            backdropFilter: "none",
            WebkitBackdropFilter: "none"
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)"
          }
        }}
        transitionDuration={300}
      >
        <div className="navbar-mobile-native">
          {/* Native iOS-style header */}
          <div className="native-drawer-header">
            <button
              className="native-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              <GrClose size={24} />
            </button>
          </div>

          {/* Menu Items */}
          <div className="native-drawer-content">
            {userData && (
              <>
                {/* User info header */}
                {Capacitor.isNativePlatform() && userData && (
                  <div
                    style={{
                      padding: "16px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.12)",
                      marginBottom: 8
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#333",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 8px"
                      }}
                    >
                      {userData?.pic1 ? (
                        <img
                          src={userData.pic1}
                          alt={userData?.firstname || "User"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <span style={{ color: "#fff", fontSize: 18, fontWeight: "600" }}>
                          {(userData?.firstname?.[0] || "").toUpperCase()}
                          {(userData?.lastname?.[0] || "").toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 16, fontWeight: "600", textAlign: "center", margin: 0, color: "#fff" }}>
                      {(Aa(userData?.firstname) + " " + Aa(userData?.lastname)).trim() || "User"}
                    </p>
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", textAlign: "center", margin: "4px 0 0 0" }}>
                      {userData?.email}
                    </p>
                  </div>
                )}

                {/* My Profile */}
                {Capacitor.isNativePlatform() && userData && (
                  <div
                    className="native-menu-item"
                    onClick={() => { setOpen(false); history.push("/user/profile"); }}
                  >
                    <span className="native-menu-text">{t("navbar.my_profile", selectedLang)}</span>
                  </div>
                )}

                {/* My Orders */}
                {Capacitor.isNativePlatform() && userData && (
                  <div
                    className="native-menu-item"
                    onClick={() => { setOpen(false); history.push("/user/orders"); }}
                  >
                    <span className="native-menu-text">{t("navbar.my_orders", selectedLang)}</span>
                  </div>
                )}

                {/* Section divider */}
                {Capacitor.isNativePlatform() && userData && (
                  <hr style={{ margin: "10px 0", border: "none", borderTop: "2px solid rgba(255,255,255,0.25)" }} />
                )}

                {/* Become Chef */}
                {Capacitor.isNativePlatform() && userData && userData?.chef === false && (
                  <div
                    className="native-menu-item"
                    onClick={() => { setOpen(false); openPartnerPage(); }}
                  >
                    <span className="native-menu-text">{t("navbar.chef", selectedLang)}</span>
                  </div>
                )}

                {/* Help Center */}
                {Capacitor.isNativePlatform() && userData && (
                  <div
                    className="native-menu-item"
                    onClick={() => { setOpen(false); history.push("/chef/iam-a-chef/help"); }}
                  >
                    <span className="native-menu-text">{t("navigation.help_center", selectedLang)}</span>
                  </div>
                )}

                {/* Back to Home */}
                {Capacitor.isNativePlatform() && userData && (
                  <div
                    className="native-menu-item"
                    onClick={() => { setOpen(false); history.push("/"); }}
                  >
                    <span className="native-menu-text">{t("navigation.go_back_home", selectedLang)}</span>
                  </div>
                )}

                {/* Sign Out */}
                {Capacitor.isNativePlatform() && userData && (
                  <div
                    className="native-menu-item"
                    onClick={() => { setOpen(false); LogOutUser(); history.push("/"); }}
                  >
                    <span className="native-menu-text" style={{ color: "#ff114d" }}>
                      {t("navigation.sign_out", selectedLang)}
                    </span>
                  </div>
                )}

                {/* Auth Buttons for unauthenticated users */}
                {!userData && (
                  <div className="native-menu-section">
                    <button
                      className="native-btn native-btn-secondary"
                      onClick={() => { setOpen(false); setOpenDialog(true); }}
                    >
                      {t("navbar.login", selectedLang)}
                    </button>
                    <button
                      className="native-btn native-btn-primary"
                      onClick={() => { setOpen(false); setOpenDialog(true); }}
                    >
                      {t("navbar.register", selectedLang)}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Language Toggle - shown for all users */}
            <div className="native-language-section">
              <LanguageToggleButton
                className="native-btn"
                style={{
                  width: "100%",
                  fontSize: "15px",
                  padding: "12px 20px",
                  borderRadius: "12px",
                  backgroundColor: "#1a1a1a",
                  color: "#ffffff",
                  border: "2px solid #444"
                }}
              />
            </div>
          </div>
        </div>
      </Drawer>

      {/* Cart Drawer Component */}
      <CartDrawer
        open={openCart}
        onClose={() => setOpenCart(false)}
        onUpdateCart={post_to_cart}
      />
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl2}
        open={openDropdown}
        onClose={handleClose2}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        style={{}}
      >
        <div
          style={{
            width: 350,
            height: 350,
            marginTop: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#cecece",
                alignSelf: "center",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {userData?.pic1 ? (
                <img
                  src={userData.pic1}
                  alt={userData?.firstname || "User"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                <span style={{ color: "#fff", fontSize: 16, fontWeight: "600" }}>
                  {(userData?.firstname?.[0] || "").toUpperCase()}
                  {(userData?.lastname?.[0] || "").toUpperCase()}
                </span>
              )}
            </div>
            <p
              style={{
                fontSize: 16,
                fontWeight: "500",
                textAlign: "center"
              }}
            >
              {userData ? (Aa(userData?.firstname) + " " + Aa(userData?.lastname)).trim() || "User" : ""}
            </p>
            <p
              style={{
                fontSize: 15,
                fontWeight: "500",
                textAlign: "center",
                marginTop: -15
              }}
            >
              {userData?.email}
            </p>
          </div>
          <hr />
          <div
            style={{
              width: "95%",
              display: "flex",
              flexWrap: "wrap"
            }}
          >
            {[
              {
                title: t("navbar.home", selectedLang),
                icon: <TbChefHat size={22.5} />,
                returnFunction: () => {
                  return history.push(`/explore/chef?date=${selectedWhen}`);
                }
              },
              {
                title: t("navbar.profile", selectedLang),
                icon: <MdAccountCircle size={22.5} />,
                returnFunction: () => {
                  return history.push("/user/profile");
                }
              },
              {
                title: t("navbar.orders", selectedLang),
                icon: <GiBackwardTime size={22.5} />,
                returnFunction: () => {
                  return history.push("/user/orders");
                }
              },
              {
                title: t("navbar.delivery_address", selectedLang),
                icon: <MdLocationPin size={22.5} />,
                returnFunction: () => {
                  return history.push("/user/profile");
                }
              }
            ].map((item, index) => (
              <div
                key={index}
                className="item-profil-user"
                onClick={() => {
                  item?.returnFunction();
                }}
              >
                {item?.icon} {item.title}
              </div>
            ))}
          </div>
          <hr />
          <div
            className="item-profil-user"
            style={{ color: "#c62828" }}
            onClick={() => {
              handleClose2();
              setDeleteAccountDialogOpen(true);
            }}
          >
            🗑️ {t("profile.delete_account", selectedLang)}
          </div>
          <div
            className="btn-get-started"
            style={{
              backgroundColor: "#ff114d",
              width: "90%"
            }}
            onClick={() => {
              handleClose2();
              LogOutUser();
              history.push("/");
            }}
          >
            {t("navbar.log_out", selectedLang)}
          </div>
        </div>
      </Menu>

      <DeleteAccountDialog
        open={deleteAccountDialogOpen}
        onClose={() => setDeleteAccountDialogOpen(false)}
        loading={deleteAccountLoading}
        onConfirm={handleDeleteAccount}
        selectedLang={selectedLang}
      />

      <LoginSignupDialog
        open={openDialog && location.pathname !== "/reset-password"}
        onClose={() => setOpenDialog(false)}
      />
    </div>
  );
};

export default Navbar;

const post_cart = (data) => ({
  type: POST_TO_CART,
  data: data
});

const post_user = (data) => ({
  type: POST_USER_DATA,
  data: data
});

const toggle_dialog = (data) => ({
  type: TOGGLE_DIALOG,
  data: data
});

const post_logout = () => ({
  type: POST_LOGOUT,
  data: null
});
